import React, { useState, useMemo } from 'react';
import { Box, Chip, Typography, TextField, Skeleton } from '@mui/material';
import { CancellationReasons } from '@EcamModel/model';

interface ReasonChipSelectProps {
    value?: CancellationReasons;
    onChange: (reason: CancellationReasons | undefined) => void;
    reasons: CancellationReasons[];
    isLoading?: boolean;
}

export default function ReasonChipSelect({ value, onChange, reasons, isLoading = false }: ReasonChipSelectProps) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredReasons = useMemo(() => {
        const indexed = reasons.map((reason, idx) => ({ reason, originalIndex: idx }));
        if (!searchTerm) return indexed;
        return indexed.filter((item) => item.reason.Reason.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [reasons, searchTerm]);

    const handleChipClick = (reason: CancellationReasons) => {
        if (value?.Id === reason.Id) {
            onChange(undefined);
        } else {
            onChange(reason);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} variant="rounded" height={40} />
                ))}
            </Box>
        );
    }

    if (reasons.length === 0) {
        return <Typography color="text.secondary">No reasons available</Typography>;
    }

    return (
        <Box>
            {/* Search Input */}
            <TextField
                placeholder="Search reasons"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                label="Search reasons"
                sx={{ mb: 2 }}
            />

            {/* Chips Grid */}
            {filteredReasons?.length ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                        alignItems: 'start',
                    }}
                >
                    {filteredReasons.map(({ reason, originalIndex }) => {
                        const isSelected = value?.Id === reason.Id;
                        return (
                            <Chip
                                key={reason.Id}
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            width: '100%',
                                            py: 0.5,
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 18,
                                                height: 18,
                                                borderRadius: '4px',
                                                bgcolor: isSelected ? 'error.main' : 'action.hover',
                                                color: isSelected ? '#fff' : 'text.secondary',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                fontFamily: 'monospace',
                                                lineHeight: 1,
                                                px: 0.3,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {originalIndex}
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                textAlign: 'left',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.3,
                                                overflowWrap: 'break-word',
                                            }}
                                        >
                                            {reason.Reason}
                                        </Box>
                                    </Box>
                                }
                                onClick={() => handleChipClick(reason)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                    }
                                }}
                                color={isSelected ? 'error' : 'default'}
                                variant="outlined"
                                clickable
                                sx={{
                                    fontSize: 14,
                                    borderStyle: 'solid',
                                    borderColor: isSelected ? 'error.main' : 'divider',
                                    color: isSelected ? 'error.main' : 'text.primary',
                                    height: 'auto',
                                    minHeight: 32,
                                    '& .MuiChip-label': {
                                        padding: '6px 12px',
                                        width: '100%',
                                        whiteSpace: 'normal',
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Typography color="text.secondary" textAlign="center">
                    No data
                </Typography>
            )}
        </Box>
    );
}
